import { useRef, useEffect, useState, useCallback } from 'react';
import type { Cover, TextZone } from '@/lib/api';
import { cornerResizeScale } from '@/lib/coverResize';
import { fontStackFor } from '@/lib/textBidi';
import { COVER_IMAGE_FRAME_W_MM, COVER_IMAGE_FRAME_H_MM } from '@/lib/coverPanelGeometry';

export interface TextZoneOverride {
  x?: number;
  y?: number;
  fontSize?: number;
  scale?: number;
  hidden?: boolean;
  fontFamily?: string;
  color?: string;
}

export interface ExtraTextZone {
  text: string;
  /** Anchor point (fraction of the cover). Which part of the text box the
   *  anchor pins is set by `align`: left → box left edge, center → box centre,
   *  right → box right edge. The box itself hugs the text, so resizing the
   *  font grows the frame with it and there's room to drag it anywhere. */
  x: number;
  y: number;
  /** Max wrap width (fraction) — long text wraps here; short text hugs. */
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  /** Horizontal alignment / anchor. Defaults to 'center'. */
  align?: 'left' | 'center' | 'right';
}

/**
 * Transform applied to the transparent design overlay (translate + scale)
 * so the customer can reposition the illustration on the front face. x/y
 * are container-relative percentages (0,0 = no offset), scale is a
 * multiplier where 1 = the original contain-fit. Persisted into
 * cover_customizations and mirrored in the PDF.
 */
export interface CoverDesignTransform {
  x: number;
  y: number;
  scale: number;
}

interface CoverThumbnailProps {
  cover: Cover;
  className?: string;
  destinationText?: string;
  yearText?: string;
  customTexts?: Record<number, string>;
  zoneOverrides?: Record<number, TextZoneOverride>;
  extraZones?: ExtraTextZone[];
  /** Overrides the front-cover background color (defaults to cover.dominant_color). */
  bgColorOverride?: string | null;
  /** When true, the cover thumbnail image is hidden so only the bg color shows. */
  hideThumbnail?: boolean;
  /** Per-order admin override image (signed URL). Takes priority over
   *  `cover.design_url`; rendered the same way (object-contain + transform). */
  frontImageSrcOverride?: string | null;
  /** Client-supplied front-cover image (signed URL) on a blank ("vierge")
   *  cover. Rendered cover-fit (object-cover) to fill the whole front panel,
   *  behind the text zones — NOT contain-fit like a transparent design. */
  blankCoverImageSrc?: string | null;
  /** Pan/zoom the client applied to their blank-cover image (translate % of
   *  the panel + scale ≥1 on the cover-fit base). Mirrored in the PDF. */
  blankCoverTransform?: CoverDesignTransform;
  /** Fires as the client drags / zooms their blank-cover image. When absent
   *  the image is display-only (flow preview, admin pages). */
  onBlankCoverTransformChange?: (t: CoverDesignTransform) => void;
  /** Drag + zoom transform applied to the design overlay. */
  designTransform?: CoverDesignTransform;
  /** Fires whenever the customer drags or zooms the design overlay. */
  onDesignTransformChange?: (t: CoverDesignTransform) => void;
  /** True when the customer has clicked the design (used by the parent
   *  to show the drag/zoom toolbar). */
  designSelected?: boolean;
  /** Fires when the customer clicks the design or clicks the cover
   *  background to deselect. */
  onDesignSelectedChange?: (selected: boolean) => void;
  interactive?: boolean;
  onZoneSelect?: (zoneKey: string | null) => void;
  onZoneChange?: (zoneIndex: number, override: TextZoneOverride) => void;
  onZoneDelete?: (zoneIndex: number) => void;
  onExtraZoneChange?: (extraIndex: number, zone: ExtraTextZone) => void;
  onExtraZoneDelete?: (extraIndex: number) => void;
  selectedZone?: string | null;
  /**
   * Called when the user types into a selected text zone (inline editing).
   * Parent dispatches the new text into the right state slot based on the
   * zone's type (destination → coverDestText, year → coverYearText,
   * custom → customTexts[idx]).
   */
  onZoneTextChange?: (zoneIndex: number, newText: string) => void;
  /** Which zone, if any, is currently in text-edit mode. Distinct from
   *  `selectedZone`: a zone can be selected (drag handles visible, draggable
   *  by the body) without its content being editable. Double-clicking a
   *  selected zone enters edit mode. */
  editingZone?: string | null;
  /** Fired when the customer double-clicks a zone to start editing the text,
   *  or null to exit. */
  onZoneEditChange?: (zoneKey: string | null) => void;
  /** True when the current user is an admin. Customers may edit the cover
   *  title (destination/year) but never delete it — the delete affordance is
   *  hidden and an empty title falls back to its default. Admins keep full
   *  control. See Permanent Fix 2 ("cover text always exists"). */
  isAdmin?: boolean;
  /** "Couverture vierge" — the client owns the design: the title/year zones
   *  show a small field label ("Titre"/"Année") and become deletable (the
   *  protected-title rule is lifted on blank only). */
  isBlankCover?: boolean;
}

const PDF_WIDTH = 1860;

/** Cover IMAGE FRAME ratio (200 × 300mm) as a CSS aspect-ratio. Must stay equal to
 *  `coverPanelGeometry().aspect`, which the cover PDF uses — if these drift,
 *  the editor crops the customer's photo differently from the print. */
export const COVER_PANEL_ASPECT = `${COVER_IMAGE_FRAME_W_MM} / ${COVER_IMAGE_FRAME_H_MM}`;

// Cover safe zone — the A4 front panel is wrapped on a rigid board, so content
// near the edge sits at the very rim of the finished cover (or the spine hinge).
// Keep editable text inside this inset (fraction of the panel ≈ 8 mm) so the
// client's title (e.g. "Paris") can't land in the trim/fold danger area. Shown
// as a faint guide in the editor and used to clamp text dragging.
const SAFE_INSET_X = 0.045; // ~9.5 mm of 210 mm
const SAFE_INSET_Y = 0.032; // ~9.5 mm of 297 mm

// The editor's font picker writes a full CSS family list into
// `override.fontFamily` (e.g. `'Fraunces', 'Cormorant Garamond', Georgia,
// serif`), but `zone.font_family` from the DB is usually a bare family name
// (e.g. `Bebas Neue`). Wrap bare names and append a generic fallback; pass
// CSS lists through verbatim. Wrapping a list in extra quotes produced
// invalid CSS and was the reason the font picker didn't visually update
// the cover text.
function toCssFontFamily(family: string | null): string {
  if (!family) return 'inherit';
  return /[,'"]/.test(family) ? family : `"${family}", sans-serif`;
}

// Trash icon as inline SVG
const TrashIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

/**
 * Renders text either as a static span (preview) or as a contentEditable
 * span (when the zone is selected for editing). The contentEditable path
 * is uncontrolled — React doesn't manage textContent on every keystroke,
 * which avoids the cursor-jumping React+contentEditable misery. We sync
 * the external `text` prop into the DOM only when it changes from the
 * outside (e.g. another editor reset) AND only when it differs from the
 * current DOM text, to leave the user's caret alone.
 */
function EditableText({
  text,
  editable,
  onChange,
  onCommit,
  spanRef,
}: {
  text: string;
  editable: boolean;
  onChange: (newText: string) => void;
  /** Optional — fires on Enter/Escape/blur so the parent can exit edit mode. */
  onCommit?: () => void;
  spanRef?: React.RefObject<HTMLSpanElement | null>;
}) {
  const internalRef = useRef<HTMLSpanElement | null>(null);
  const setRef = useCallback((el: HTMLSpanElement | null) => {
    internalRef.current = el;
    if (spanRef) (spanRef as { current: HTMLSpanElement | null }).current = el;
  }, [spanRef]);

  // Sync external text → DOM only when the value actually differs. Skipping
  // the no-op write keeps the user's text-selection caret in place.
  useEffect(() => {
    const el = internalRef.current;
    if (!el) return;
    if ((el.textContent ?? '') !== text) {
      el.textContent = text;
    }
  }, [text, editable]);

  // When the user starts editing, focus + select-all so they can just type
  // to replace the placeholder text.
  useEffect(() => {
    if (!editable) return;
    const el = internalRef.current;
    if (!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editable]);

  return (
    <span
      ref={setRef}
      className="outline-none"
      contentEditable={editable}
      // 'plaintext-only' would be ideal but Safari treats it inconsistently.
      // We sanitise via textContent in onInput so the editor still only
      // sees plain text even when the user pastes rich content.
      suppressContentEditableWarning
      spellCheck={false}
      onInput={() => onChange(internalRef.current?.textContent ?? '')}
      // Bug 1 fix (2026-07-05) — paste as PLAIN text so the cover zone's chosen
      // font/colour always wins; a pasted rich <span style> would override it.
      onPaste={(e) => {
        e.preventDefault();
        const t = e.clipboardData.getData('text/plain');
        if (!t) return;
        try { document.execCommand('insertText', false, t); }
        catch {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0); r.deleteContents();
            const n = document.createTextNode(t); r.insertNode(n);
            r.setStartAfter(n); r.collapse(true); sel.removeAllRanges(); sel.addRange(r);
          }
        }
        onChange(internalRef.current?.textContent ?? '');
      }}
      onMouseDown={(e) => { if (editable) e.stopPropagation(); }}
      onTouchStart={(e) => { if (editable) e.stopPropagation(); }}
      onBlur={() => onCommit?.()}
      onKeyDown={(e) => {
        // Multi-line: plain Enter inserts a newline (default contentEditable
        // behaviour). Shift+Enter and Cmd/Ctrl+Enter explicitly commit so
        // keyboard-driven customers still have a finish gesture. Escape
        // always exits.
        if (e.key === 'Enter' && (e.shiftKey || e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          (e.target as HTMLElement).blur();
          return;
        }
        if (e.key === 'Escape') { (e.target as HTMLElement).blur(); }
      }}
      style={{
        overflow: 'visible',
        // Allow line breaks + word wrapping so multi-line text doesn't run
        // off the end of the zone. The customer can grow the zone via the
        // corner handle if they need more room.
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        display: 'inline-block',
      }}
    />
  );
}

export function CoverThumbnail({
  cover,
  className = '',
  destinationText,
  yearText,
  customTexts,
  zoneOverrides,
  extraZones,
  bgColorOverride,
  hideThumbnail = false,
  frontImageSrcOverride,
  blankCoverImageSrc,
  blankCoverTransform,
  onBlankCoverTransformChange,
  designTransform,
  onDesignTransformChange,
  designSelected,
  onDesignSelectedChange,
  interactive = false,
  onZoneSelect,
  onZoneChange,
  onZoneDelete,
  onExtraZoneChange,
  onExtraZoneDelete,
  selectedZone,
  onZoneTextChange,
  editingZone,
  onZoneEditChange,
  isAdmin = false,
  isBlankCover = false,
}: CoverThumbnailProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(300);
  const [containerHeight, setContainerHeight] = useState(400);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setContainerWidth(rect.width);
      setContainerHeight(rect.height);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const zones = cover.text_zones ?? [];
  const fontScale = containerWidth / PDF_WIDTH;

  // ── Blank-cover image pan/zoom (client's own front photo) ────────────────
  // Cover-fit base × the persisted transform. Drag pans; a small −/％/+ pill
  // (rendered when selected) zooms. Display-only when no onChange is supplied.
  const btx = blankCoverTransform?.x ?? 0;
  const bty = blankCoverTransform?.y ?? 0;
  const btScale = blankCoverTransform?.scale ?? 1;
  const [blankSelected, setBlankSelected] = useState(false);
  const blankDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const blankInteractive = interactive && !!onBlankCoverTransformChange;
  const onBlankPointerDown = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
    if (!blankInteractive) return;
    e.preventDefault(); e.stopPropagation();
    setBlankSelected(true);
    blankDragRef.current = { startX: e.clientX, startY: e.clientY, origX: btx, origY: bty };
    const el = e.currentTarget;
    try { el.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    const move = (ev: PointerEvent) => {
      const d = blankDragRef.current;
      if (!d) return;
      const nx = d.origX + ((ev.clientX - d.startX) / Math.max(1, containerWidth)) * 100;
      const ny = d.origY + ((ev.clientY - d.startY) / Math.max(1, containerHeight)) * 100;
      // Clamp so the cover-fit image (scaled ≥1) can't be dragged off-panel:
      // at scale s the overflow each side is (s-1)/2 of the panel (plus the
      // cover-fit overscan, which we conservatively ignore → tight clamp).
      const max = Math.max(0, ((btScale - 1) / 2) * 100 + 15);
      onBlankCoverTransformChange!({
        x: Math.max(-max, Math.min(max, nx)),
        y: Math.max(-max, Math.min(max, ny)),
        scale: btScale,
      });
    };
    const up = () => {
      blankDragRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [blankInteractive, btx, bty, btScale, containerWidth, containerHeight, onBlankCoverTransformChange]);
  // Clicking anywhere else deselects the blank image (same pattern as the
  // design overlay's background-click deselect).
  useEffect(() => {
    if (!blankSelected) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-blank-cover-img]') || t.closest('[data-blank-zoom-pill]')) return;
      setBlankSelected(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [blankSelected]);

  // ── Design overlay drag + zoom ───────────────────────────────────────────
  // Customer can drag the transparent illustration to reposition it and
  // wheel to zoom. We only mount the listeners when the parent supplied an
  // `onDesignTransformChange` callback — non-interactive renders (cover
  // picker thumbnails, library cards) stay static.
  const tx = designTransform?.x ?? 0;
  const ty = designTransform?.y ?? 0;
  const tScale = designTransform?.scale ?? 1;
  const designDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  // D78 (2026-05-21): natural dimensions of the design PNG. Captured via
  // onLoad so the selection frame can be sized to the design's actual
  // visible bbox (after `object-contain` letterboxing + the customer's
  // translate/scale), not to the cover container. Without this the
  // frame wrapped the whole cover face — the customer asked for it to
  // wrap the design itself.
  const [designNaturalDims, setDesignNaturalDims] = useState<{ w: number; h: number } | null>(null);
  // Container size in pixels (captured via ResizeObserver) so we can
  // turn the design's intrinsic aspect into a % of the container.
  const [designContainerSize, setDesignContainerSize] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    if (!interactive) return;
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      setDesignContainerSize({ w: el.clientWidth, h: el.clientHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [interactive]);

  // Clamp the design overlay's {x,y} so its visible bounding box stays in a
  // sensible relationship with the cover SAFE ZONE — the same rect the text
  // is clamped into. Reuses the exact object-contain bbox math used by the
  // selection frame below (visW/visH → scaledW/scaledH → centre ± half).
  //
  // Per axis we pick the rule by comparing the design's extent to the safe
  // zone's extent on that axis:
  //   • design extent ≥ safe-zone extent  → "must cover": the bbox can't be
  //     pulled so far that the safe zone shows bare board (bboxLeft ≤ safeLeft
  //     AND bboxRight ≥ safeRight). This is the common case — the design fills
  //     the panel.
  //   • design extent <  safe-zone extent → "stay inside": the (small) design
  //     can't be dragged out past the safe-zone edges (like text).
  //
  // If the design's natural size or the container size isn't known yet we
  // skip clamping (return the candidate unchanged) so nothing breaks on first
  // paint. Non-interactive renders never call this.
  const clampDesignTransform = useCallback(
    (x: number, y: number, scale: number): CoverDesignTransform => {
      if (!designNaturalDims || !designContainerSize) return { x, y, scale };
      const cw = designContainerSize.w || 1;
      const ch = designContainerSize.h || 1;
      const cAR = cw / ch;
      const dAR = designNaturalDims.w / designNaturalDims.h;
      // object-contain bbox inside the img (which fills the container), in %.
      let visW: number, visH: number;
      if (dAR > cAR) {
        visW = 100;
        visH = 100 * (cAR / dAR);
      } else {
        visW = 100 * (dAR / cAR);
        visH = 100;
      }
      const scaledW = visW * scale;
      const scaledH = visH * scale;

      const safeLeft = SAFE_INSET_X * 100;
      const safeRight = 100 - SAFE_INSET_X * 100;
      const safeTop = SAFE_INSET_Y * 100;
      const safeBottom = 100 - SAFE_INSET_Y * 100;
      const safeW = safeRight - safeLeft;
      const safeH = safeBottom - safeTop;

      // X axis. Centre of the design = x + 50 (translate moves the whole img).
      let clampedX = x;
      if (scaledW >= safeW) {
        // must cover: x ∈ [safeRight - 50 - scaledW/2, safeLeft - 50 + scaledW/2]
        const lo = safeRight - 50 - scaledW / 2;
        const hi = safeLeft - 50 + scaledW / 2;
        clampedX = Math.max(lo, Math.min(hi, x));
      } else {
        // stay inside: x ∈ [safeLeft - 50 + scaledW/2, safeRight - 50 - scaledW/2]
        const lo = safeLeft - 50 + scaledW / 2;
        const hi = safeRight - 50 - scaledW / 2;
        clampedX = Math.max(lo, Math.min(hi, x));
      }

      // Y axis (same logic).
      let clampedY = y;
      if (scaledH >= safeH) {
        const lo = safeBottom - 50 - scaledH / 2;
        const hi = safeTop - 50 + scaledH / 2;
        clampedY = Math.max(lo, Math.min(hi, y));
      } else {
        const lo = safeTop - 50 + scaledH / 2;
        const hi = safeBottom - 50 - scaledH / 2;
        clampedY = Math.max(lo, Math.min(hi, y));
      }

      return { x: clampedX, y: clampedY, scale };
    },
    [designNaturalDims, designContainerSize],
  );

  // Self-heal an already-saved off-screen design/image on first load. A bad
  // persisted transform (e.g. x≈-86, y≈-99 on order #1081) pushes the artwork
  // fully off the panel → invisible AND un-grabbable, a dead end that survives
  // reloads. Once the design's natural size + container size are known (and this
  // is the interactive editor), clamp the CURRENT transform into the safe zone;
  // if the clamp moved it meaningfully (i.e. it was out of bounds = off-screen),
  // commit the corrected value ONCE so the parent autosaves it. clampDesign-
  // Transform returns in-bounds transforms UNCHANGED, so the epsilon "differs"
  // check naturally no-ops for normal covers — they are never shifted on load.
  const selfHealedRef = useRef(false);
  useEffect(() => {
    if (selfHealedRef.current) return;
    if (!interactive || !onDesignTransformChange) return;
    if (!designNaturalDims || !designContainerSize) return;
    const clamped = clampDesignTransform(tx, ty, tScale);
    const EPS = 0.5; // container-% tolerance; sub-pixel diffs aren't off-screen
    if (Math.abs(clamped.x - tx) > EPS || Math.abs(clamped.y - ty) > EPS) {
      onDesignTransformChange(clamped);
    }
    // Mark as done either way — we only auto-correct at most once per mount and
    // never fight the customer's deliberate drags afterwards.
    selfHealedRef.current = true;
  }, [interactive, onDesignTransformChange, designNaturalDims, designContainerSize, tx, ty, tScale, clampDesignTransform]);

  // Pointer events (not mouse) so drag-to-move works on touch + pen as well as
  // mouse. `touch-action: none` on the img stops the browser from scrolling/
  // pinch-zooming the page mid-drag. setPointerCapture keeps the gesture even
  // if the finger slides off the artwork.
  const onDesignPointerDown = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
    if (!interactive || !onDesignTransformChange) return;
    e.preventDefault();
    e.stopPropagation();
    // Clicking the artwork selects it — the parent uses this to show the
    // drag/zoom toolbar. Selection survives the drag itself; the parent
    // clears it when the customer clicks the cover background.
    onDesignSelectedChange?.(true);
    try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* defensive */ }
    designDragRef.current = { startX: e.clientX, startY: e.clientY, origX: tx, origY: ty };
    const onMove = (ev: PointerEvent) => {
      const d = designDragRef.current;
      if (!d) return;
      const w = containerRef.current?.clientWidth ?? 1;
      const h = containerRef.current?.clientHeight ?? 1;
      const dx = ((ev.clientX - d.startX) / w) * 100;
      const dy = ((ev.clientY - d.startY) / h) * 100;
      // Clamp the committed value to the safe zone (see clampDesignTransform).
      onDesignTransformChange(clampDesignTransform(d.origX + dx, d.origY + dy, tScale));
    };
    const onUp = () => {
      designDragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [interactive, onDesignTransformChange, onDesignSelectedChange, tx, ty, tScale, clampDesignTransform]);

  // D78 (2026-05-21): corner-handle resize for the cover design. Matches
  // the Canva-style 4-corner UX the customer asked for ("same as text").
  // Distance-from-centre scaling — drag a corner OUT to grow the design,
  // IN to shrink. All four corners share the same gesture math; the
  // centre stays anchored so it feels like a uniform zoom. Clamped to
  // the same [0.3, 3] range as the wheel/+- buttons.
  const onDesignCornerDown = useCallback((e: React.PointerEvent) => {
    if (!interactive || !onDesignTransformChange) return;
    e.preventDefault();
    e.stopPropagation();
    try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* defensive */ }
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const startDist = Math.max(1, Math.hypot(e.clientX - cx, e.clientY - cy));
    const startScale = tScale;
    const onMove = (ev: PointerEvent) => {
      const dist = Math.max(1, Math.hypot(ev.clientX - cx, ev.clientY - cy));
      const next = Math.max(0.3, Math.min(3, startScale * (dist / startDist)));
      const scale = Math.round(next * 100) / 100;
      // Re-clamp x/y for the NEW scale — shrinking can pull the bbox out of
      // (or off) the safe zone, so the position has to be re-validated too.
      onDesignTransformChange(clampDesignTransform(tx, ty, scale));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [interactive, onDesignTransformChange, tx, ty, tScale, clampDesignTransform]);

  // Wheel-to-zoom over the design. React's onWheel is passive, so to call
  // preventDefault (stop the page from scrolling under the cursor) we add a
  // native listener via the container ref. Clamped to [0.3, 3]× so the
  // design can't shrink to nothing or blow out past the bleed.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !interactive || !onDesignTransformChange) return;
    const onWheel = (ev: WheelEvent) => {
      // Plain wheel scroll on a page would scroll the document; we only
      // intercept when the cursor is over the design img specifically so
      // surrounding scrolling still works.
      const target = ev.target as HTMLElement | null;
      if (!target || target.dataset.coverDesign !== 'true') return;
      ev.preventDefault();
      const next = Math.max(0.3, Math.min(3, tScale * (ev.deltaY < 0 ? 1.05 : 1 / 1.05)));
      // Re-clamp x/y for the new scale so zooming out can't reveal bare board.
      onDesignTransformChange(clampDesignTransform(tx, ty, next));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [interactive, onDesignTransformChange, tx, ty, tScale, clampDesignTransform]);

  // RENDER PARITY (2026-07-29): the container was `aspect-[3/4]` (0.750) while
  // the physical front board is 200 × 303mm (0.660). With object-cover on the
  // customer's photo a 0.750 box crops a different part of the image than the
  // PDF's cover-fit does — so what they framed on screen was not what printed.
  // The ratio now comes from the same board spec the PDF uses.
  return (
    <div
      ref={containerRef}
      // Admin night mode: a light island — a cover is never re-coloured (index.css › ADMIN NIGHT MODE).
      data-adm-media=""
      className={`relative overflow-hidden ${className}`}
      style={{
        aspectRatio: COVER_PANEL_ASPECT,
        backgroundColor: bgColorOverride ?? cover.dominant_color ?? '#f3f4f6',
      }}
      onClick={interactive ? (e) => {
        const isBgClick = e.target === e.currentTarget;
        const isImgClick = (e.target as HTMLElement).tagName === 'IMG';
        if (isBgClick || isImgClick) {
          onZoneSelect?.(null);
          // Background click also clears the design selection so the toolbar
          // disappears. Clicking the design itself flips designSelected
          // back on via onDesignPointerDown.
          if (isBgClick) onDesignSelectedChange?.(false);
        }
      } : undefined}
    >
      {/* Client blank-cover image — the customer's OWN front-cover photo on a
          "vierge" cover. Fills the whole panel cover-fit (object-cover) behind
          the text zones, pan/zoomable via blankCoverTransform (PDF-mirrored).
          Takes priority over the design/thumbnail path; the eye toggle
          (`hideThumbnail`) still hides it. */}
      {!hideThumbnail && blankCoverImageSrc ? (
        <>
          <img
            src={blankCoverImageSrc}
            alt=""
            data-blank-cover-img="true"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
            loading="lazy"
            decoding="async"
            onPointerDown={blankInteractive ? onBlankPointerDown : undefined}
            style={{
              transform: `translate(${btx}%, ${bty}%) scale(${btScale})`,
              transformOrigin: 'center center',
              cursor: blankInteractive ? 'move' : undefined,
              touchAction: blankInteractive ? 'none' : undefined,
              outline: blankSelected ? '2px dashed #C9A24A' : 'none',
              outlineOffset: -2,
            }}
          />
          {/* Zoom pill — appears when the client selects their photo. −/+ zoom
              (1×–3×, drag pans) + reset. Mirrors the design toolbar pattern. */}
          {blankInteractive && blankSelected && (
            <div
              data-blank-zoom-pill="true"
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 rounded-full bg-white px-1 py-1"
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)', zIndex: 20 }}
            >
              <button
                type="button"
                onClick={() => onBlankCoverTransformChange!({ x: btx, y: bty, scale: Math.max(1, Math.round((btScale / 1.15) * 100) / 100) })}
                className="w-7 h-7 rounded-full flex items-center justify-center text-base hover:bg-gray-100"
                style={{ color: '#1F2937' }}
                title="Réduire"
              >−</button>
              <span className="text-[11px] tabular-nums w-9 text-center select-none" style={{ color: '#1F2937' }}>
                {Math.round(btScale * 100)}%
              </span>
              <button
                type="button"
                onClick={() => onBlankCoverTransformChange!({ x: btx, y: bty, scale: Math.min(3, Math.round((btScale * 1.15) * 100) / 100) })}
                className="w-7 h-7 rounded-full flex items-center justify-center text-base hover:bg-gray-100"
                style={{ color: '#1F2937' }}
                title="Agrandir"
              >+</button>
              <div className="w-px h-4 mx-0.5" style={{ background: 'rgba(0,0,0,0.08)' }} />
              <button
                type="button"
                onClick={() => onBlankCoverTransformChange!({ x: 0, y: 0, scale: 1 })}
                className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100 text-[11px]"
                style={{ color: '#6B7280' }}
                title="Réinitialiser"
              >↺</button>
            </div>
          )}
        </>
      ) : null}

      {/* Front-cover artwork. When `design_url` is set we use the transparent
          PNG (illustration only) — the container's bg color shows through, so
          per-face color picks visibly recolor the cover. Older rows that only
          have `thumbnail_url` use it as a fully-baked image which won't react
          to color changes; the editor's eye toggle (`hideThumbnail`) lets the
          customer remove it. */}
      {!hideThumbnail && !blankCoverImageSrc && (frontImageSrcOverride ?? cover.design_url) ? (
        <>
          <img
            src={frontImageSrcOverride ?? cover.design_url ?? undefined}
            alt=""
            data-cover-design="true"
            className="absolute inset-0 w-full h-full object-contain"
            draggable={false}
            loading="lazy"
            decoding="async"
            onLoad={(e) => {
              const t = e.currentTarget;
              if (t.naturalWidth && t.naturalHeight) {
                setDesignNaturalDims({ w: t.naturalWidth, h: t.naturalHeight });
              }
            }}
            onPointerDown={onDesignPointerDown}
            style={{
              transform: `translate(${tx}%, ${ty}%) scale(${tScale})`,
              transformOrigin: 'center center',
              cursor: interactive && onDesignTransformChange ? 'move' : undefined,
              touchAction: 'none',
            }}
          />
          {/* D78 (2026-05-21): selection frame + 4 corner handles around
              the DESIGN's actual visible bbox. The design PNG is fitted
              via `object-contain` so its visual area is letterboxed
              inside the img element; the customer's translate/scale
              then move + grow that letterbox. We compute the post-
              transform bbox in container % and clamp it to the cover
              bounds so the frame stays visible when the design overflows
              the cover (overflow:hidden would otherwise clip it). */}
          {interactive && designSelected && onDesignTransformChange && designNaturalDims && designContainerSize && (() => {
            const cw = designContainerSize.w || 1;
            const ch = designContainerSize.h || 1;
            const cAR = cw / ch;
            const dAR = designNaturalDims.w / designNaturalDims.h;
            // object-contain bbox inside the img element (which fills
            // the container). Express as % of container.
            let visW: number, visH: number;
            if (dAR > cAR) {
              // Design wider than container → fits width, letterbox top/bottom
              visW = 100;
              visH = 100 * (cAR / dAR);
            } else {
              visW = 100 * (dAR / cAR);
              visH = 100;
            }
            const scaledW = visW * tScale;
            const scaledH = visH * tScale;
            // Centre of the design in container % (translate moves the
            // whole img element by tx/ty % of container).
            const cxPct = tx + 50;
            const cyPct = ty + 50;
            // Raw bbox (may extend past container).
            const rawLeft = cxPct - scaledW / 2;
            const rawTop = cyPct - scaledH / 2;
            const rawRight = cxPct + scaledW / 2;
            const rawBottom = cyPct + scaledH / 2;
            // Clamp to container so the frame stays inside the visible
            // crop (the cover's overflow:hidden would clip anything past
            // 0/100).
            const frameLeft = Math.max(0, rawLeft);
            const frameTop = Math.max(0, rawTop);
            const frameRight = Math.min(100, rawRight);
            const frameBottom = Math.min(100, rawBottom);
            const frameWidth = Math.max(0, frameRight - frameLeft);
            const frameHeight = Math.max(0, frameBottom - frameTop);
            if (frameWidth <= 0 || frameHeight <= 0) return null;
            return (
              <>
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${frameLeft}%`,
                    top: `${frameTop}%`,
                    width: `${frameWidth}%`,
                    height: `${frameHeight}%`,
                    border: '2px solid #C9A24A',
                    boxShadow: '0 0 0 1px rgba(201,162,74,0.3)',
                    zIndex: 5,
                  }}
                />
                {[
                  { left: frameLeft,  top: frameTop,    translate: 'translate(-50%, -50%)', cursor: 'nwse-resize' as const },
                  { left: frameRight, top: frameTop,    translate: 'translate(-50%, -50%)', cursor: 'nesw-resize' as const },
                  { left: frameLeft,  top: frameBottom, translate: 'translate(-50%, -50%)', cursor: 'nesw-resize' as const },
                  { left: frameRight, top: frameBottom, translate: 'translate(-50%, -50%)', cursor: 'nwse-resize' as const },
                ].map((h, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full"
                    style={{
                      left: `${h.left}%`,
                      top: `${h.top}%`,
                      width: 14, height: 14,
                      transform: h.translate,
                      background: '#fff',
                      border: '2px solid #C9A24A',
                      cursor: h.cursor,
                      pointerEvents: 'auto',
                      touchAction: 'none',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      zIndex: 6,
                    }}
                    onPointerDown={onDesignCornerDown}
                  />
                ))}
              </>
            );
          })()}
        </>
      ) : !hideThumbnail && !blankCoverImageSrc && !frontImageSrcOverride && cover.thumbnail_url ? (
        // Baked legacy cover image. Render ONLY when no higher-priority front
        // image exists — the customer's own photo (blankCoverImageSrc) and the
        // admin override both fully replace the catalogue art. This mirrors the
        // PDF's single-image precedence (pdfGenerator: blankCover ?? design ??
        // thumbnail); without this guard the baked thumbnail painted ON TOP of
        // the customer's photo in the editor (editor ≠ PDF). See render-parity.
        <img
          src={cover.thumbnail_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      ) : null}

      {/* Safe-zone guide (editor only) — keep titles inside this faint box so
          they don't land in the trim/fold when the A4 panel is wrapped on the
          rigid board. Text dragging is clamped to the same inset. */}
      {interactive && (
        <div
          aria-hidden
          className="pointer-events-none absolute border border-dashed border-brand-400/50 rounded-[2px]"
          style={{
            left: `${SAFE_INSET_X * 100}%`,
            top: `${SAFE_INSET_Y * 100}%`,
            right: `${SAFE_INSET_X * 100}%`,
            bottom: `${SAFE_INSET_Y * 100}%`,
            zIndex: 4,
          }}
        />
      )}

      {zones.map((zone, i) => {
        const override = zoneOverrides?.[i];
        if (override?.hidden) return null;
        return (
          <TextOverlay
            key={`zone-${i}`}
            zone={zone}
            fontScale={fontScale}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            destinationText={destinationText}
            yearText={yearText}
            customText={customTexts?.[i]}
            override={override}
            interactive={interactive}
            isSelected={selectedZone === String(i)}
            isEditing={editingZone === String(i)}
            onSelect={() => onZoneSelect?.(String(i))}
            onStartEditing={() => onZoneEditChange?.(String(i))}
            onStopEditing={() => onZoneEditChange?.(null)}
            onChange={(ovr) => onZoneChange?.(i, ovr)}
            onDelete={() => onZoneDelete?.(i)}
            onTextChange={onZoneTextChange ? (newText) => onZoneTextChange(i, newText) : undefined}
            isAdmin={isAdmin}
            isBlankCover={isBlankCover}
          />
        );
      })}

      {extraZones?.map((ez, i) => (
        <ExtraTextOverlay
          key={`extra-${i}`}
          zone={ez}
          fontScale={fontScale}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          interactive={interactive}
          isSelected={selectedZone === `extra-${i}`}
          onSelect={() => onZoneSelect?.(`extra-${i}`)}
          onChange={(updated) => onExtraZoneChange?.(i, updated)}
          onDelete={() => onExtraZoneDelete?.(i)}
        />
      ))}
    </div>
  );
}

function TextOverlay({
  zone,
  fontScale,
  containerWidth,
  containerHeight,
  destinationText,
  yearText,
  customText,
  override,
  interactive,
  isSelected,
  isEditing,
  onSelect,
  onStartEditing,
  onStopEditing,
  onChange,
  onDelete,
  onTextChange,
  isAdmin = false,
  isBlankCover = false,
}: {
  zone: TextZone;
  fontScale: number;
  containerWidth: number;
  containerHeight: number;
  destinationText?: string;
  yearText?: string;
  customText?: string;
  override?: TextZoneOverride;
  interactive: boolean;
  isSelected: boolean;
  /** Customers cannot delete the destination/year title; admins can. */
  isAdmin?: boolean;
  /** "Couverture vierge" — the customer OWNS the whole design: title/year get
   *  a small field label ("Titre"/"Année") and become deletable (the protected-
   *  title rule is lifted; catalogue covers keep Permanent Fix 2). */
  isBlankCover?: boolean;
  /** Selection enables dragging the body + corner handles; editing turns
   *  the contentEditable span on. Click selects, dblclick edits. */
  isEditing: boolean;
  onSelect: () => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onChange: (ovr: TextZoneOverride) => void;
  onDelete: () => void;
  /** When provided + isEditing, the text becomes contentEditable in place. */
  onTextChange?: (newText: string) => void;
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Inner wrapper that hugs the rendered text — the corner handles anchor to
  // this box, so its centre is the pivot for the radial resize gesture.
  const boxRef = useRef<HTMLDivElement>(null);
  const [autoScale, setAutoScale] = useState(1);
  const dragState = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  // The destination/year title is mandatory on the customer-facing cover
  // (Permanent Fix 2). For customers, an empty title falls back to the zone's
  // default_text so the cover never renders blank — except while they're
  // actively editing it (so they can clear and retype). Admins keep the old
  // "respect an intentionally-empty edit" behaviour and may leave it blank.
  // On a BLANK cover the client owns the design → the protection is lifted
  // (title/year deletable, empties stay empty).
  const isProtectedTitle = (zone.type === 'destination' || zone.type === 'year') && !isAdmin && !isBlankCover;
  let text: string;
  if (zone.type === 'destination' && destinationText !== undefined) {
    text = destinationText;
  } else if (zone.type === 'year' && yearText !== undefined) {
    text = yearText;
  } else if (zone.type === 'custom' && customText !== undefined) {
    text = customText;
  } else {
    text = zone.default_text || '';
  }

  const effectiveX = override?.x ?? zone.x;
  const effectiveY = override?.y ?? zone.y;
  const manualScale = override?.scale ?? 1;
  const baseFontSize = override?.fontSize ?? zone.font_size ?? 32;
  const effectiveFontFamily = override?.fontFamily ?? zone.font_family ?? null;
  const effectiveColor = override?.color ?? zone.color ?? '#ffffff';

  // Only auto-scale when user hasn't manually adjusted scale
  const userHasManualScale = override?.scale !== undefined && override.scale !== 1;

  useEffect(() => {
    // Skip auto-scale if user manually set a scale
    if (userHasManualScale) {
      setAutoScale(1);
      return;
    }
    if (!textRef.current || !containerRef.current || !text) {
      setAutoScale(1);
      return;
    }

    const boxWidth = zone.width * containerWidth;
    const boxHeight = zone.height * containerHeight;
    const fontSize = baseFontSize * fontScale * manualScale;

    // Measure against the actual zone width so multi-line text wraps the
    // same way it'll render. white-space: pre-wrap honours hard newlines.
    const measureSpan = document.createElement('span');
    measureSpan.style.cssText = `
      position: absolute; visibility: hidden; white-space: pre-wrap;
      max-width: ${boxWidth}px;
      word-break: break-word;
      font-family: ${toCssFontFamily(effectiveFontFamily)};
      font-size: ${fontSize}px; font-weight: 400; letter-spacing: 0.01em; line-height: 1.1;
      display: inline-block;
    `;
    measureSpan.textContent = text;
    document.body.appendChild(measureSpan);
    const textWidth = measureSpan.offsetWidth;
    const textHeight = measureSpan.offsetHeight;
    document.body.removeChild(measureSpan);

    const usableWidth = boxWidth * 0.95;
    const usableHeight = boxHeight * 0.95;
    let scale = 1;
    if (textWidth > usableWidth && usableWidth > 0) scale = Math.min(scale, usableWidth / textWidth);
    if (textHeight > usableHeight && usableHeight > 0) scale = Math.min(scale, usableHeight / textHeight);
    scale = Math.max(scale, 0.3);
    setAutoScale(scale);
  }, [text, baseFontSize, fontScale, manualScale, userHasManualScale, zone.width, zone.height, effectiveFontFamily, containerWidth, containerHeight]);

  // ── Drag + resize handlers ────────────────────────────────────────────────
  // These useCallback hooks MUST be declared before the early returns below.
  // React #310 fix (2026-07-01): they used to sit AFTER the `!text` early
  // return, so adding a title (empty→non-empty text) skipped/added them between
  // renders → "rendered more hooks than the previous render" → editor crash on
  // typing a cover title. Hooks run unconditionally; only the render returns.
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!interactive || !isSelected) return;
    e.preventDefault(); e.stopPropagation();
    dragState.current = { startX: e.clientX, startY: e.clientY, origX: effectiveX, origY: effectiveY };
    const handleMove = (ev: MouseEvent) => {
      if (!dragState.current) return;
      const dx = (ev.clientX - dragState.current.startX) / containerWidth;
      const dy = (ev.clientY - dragState.current.startY) / containerHeight;
      // Clamp inside the safe zone so text can't be dragged into the trim/fold.
      const minX = SAFE_INSET_X, maxX = Math.max(minX, 1 - SAFE_INSET_X - zone.width);
      const minY = SAFE_INSET_Y, maxY = Math.max(minY, 1 - SAFE_INSET_Y - zone.height);
      onChange({ ...override, x: Math.max(minX, Math.min(maxX, dragState.current.origX + dx)), y: Math.max(minY, Math.min(maxY, dragState.current.origY + dy)) });
    };
    const handleUp = () => { dragState.current = null; window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
    window.addEventListener('mousemove', handleMove); window.addEventListener('mouseup', handleUp);
  }, [interactive, isSelected, effectiveX, effectiveY, containerWidth, containerHeight, zone.width, zone.height, override, onChange]);

  // Corner-handle resize. Center-anchored radial scaling (same gesture as the
  // cover artwork's onDesignCornerDown): the new scale tracks the ratio of
  // current-distance-from-centre to start-distance-from-centre. Both distances
  // are screen pixels, so it follows the cursor 1:1 at any canvas zoom — the
  // old `dx / 80` divisor ignored fontScale + the large cover font sizes, so
  // the text outran the mouse and jumped. We seed startScale with the CURRENT
  // rendered scale (manualScale × autoScale) so the drag begins seamlessly from
  // the size on screen instead of snapping when autoScale gets pinned to 1.
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!interactive) return;
    e.preventDefault(); e.stopPropagation();
    const rect = (boxRef.current ?? containerRef.current)?.getBoundingClientRect();
    const center = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : { x: e.clientX, y: e.clientY };
    const start = { x: e.clientX, y: e.clientY };
    const startScale = manualScale * autoScale;
    const handleMove = (ev: MouseEvent) => {
      const next = cornerResizeScale(center, start, { x: ev.clientX, y: ev.clientY }, startScale, 0.2, 5);
      onChange({ ...override, scale: Math.round(next * 100) / 100 });
    };
    const handleUp = () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
    window.addEventListener('mousemove', handleMove); window.addEventListener('mouseup', handleUp);
  }, [interactive, manualScale, autoScale, override, onChange]);

  // Empty + non-interactive ⇒ nothing to render. While editing we keep the
  // box mounted so the contentEditable span stays focusable as the user
  // deletes the last character.
  const editableNow = !!(isEditing && interactive && onTextChange);
  // Customer + protected title left empty (and not mid-edit) → show the
  // default so the cover title never disappears. The persisted-state restore
  // lives in AlbumEditorPage (onZoneEditChange); this is the render safety net.
  if (isProtectedTitle && !editableNow && !text) {
    text = zone.default_text || '';
  }
  // Blank ("vierge") cover + empty zone → GHOST placeholder: the default text
  // rendered faint so the client can still find + tap the field and type.
  // Editor chrome only — the PDF prints the real (empty) value, never this.
  let isGhost = false;
  if (isBlankCover && interactive && !text && !editableNow
      && (zone.type === 'destination' || zone.type === 'year')) {
    text = zone.default_text || (zone.type === 'year' ? 'Année' : 'Titre');
    isGhost = true;
  }
  if (!text && !editableNow) return null;

  const finalFontSize = Math.max(6, baseFontSize * fontScale * manualScale * autoScale);

  return (
    <div
      ref={containerRef}
      className={`absolute flex items-center ${interactive ? '' : 'pointer-events-none'} select-none`}
      style={{
        left: `${effectiveX * 100}%`, top: `${effectiveY * 100}%`,
        width: `${zone.width * 100}%`, height: `${zone.height * 100}%`,
        opacity: isGhost ? 0.4 : 1,
        color: effectiveColor,
        fontSize: `${finalFontSize}px`,
        // Arabic gets the named face the PDF draws with (textBidi.fontStackFor), and
        // each line reads in its own direction — editor = print (2026-10-26).
        fontFamily: fontStackFor(toCssFontFamily(effectiveFontFamily), text),
        unicodeBidi: 'plaintext',
        fontWeight: 400, lineHeight: 1.1, letterSpacing: '0.01em',
        justifyContent: zone.align === 'left' ? 'flex-start' : zone.align === 'right' ? 'flex-end' : 'center',
        zIndex: isSelected ? 10 : 1,
        // Cursor reads the mode: move when selected (drag the body), text
        // when editing the content, pointer otherwise. The customer learns
        // by seeing — no extra tooltip required.
        cursor: !interactive
          ? undefined
          : editableNow
            ? 'text'
            : isSelected
              ? 'move'
              : 'pointer',
      }}
      onClick={interactive ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
      onDoubleClick={interactive && onTextChange ? (e) => { e.stopPropagation(); onStartEditing(); } : undefined}
      // Drag the text box anywhere on the cover whenever the zone is selected.
      // We do NOT attach the drag while editing — the customer is positioning
      // their cursor inside the text and needs free mousedown for that.
      onMouseDown={interactive && isSelected && !editableNow ? handleDragStart : undefined}
    >
      {/* Selection wrapper hugs the rendered text (inline-block sizes to
          the <span>'s content). The dashed outline + corner handles + delete
          button anchor to THIS box, not the zone-sized container above, so
          the controls cling to short text instead of floating in the
          empty corners of a wide zone. */}
      <div
        ref={boxRef}
        className="relative"
        style={{
          display: 'inline-block',
          outline: isSelected ? '2px dashed #3b82f6' : 'none',
          outlineOffset: '2px',
          maxWidth: '100%',
        }}
      >
        {/* Field label — blank ("vierge") cover only: a small chip naming what
            this zone is ("Titre" / "Année") so the client knows what to fill.
            Quiet by default, solid while the zone is selected. Never printed
            (editor chrome only — the PDF renders zones, not this chip). */}
        {isBlankCover && interactive && (zone.type === 'destination' || zone.type === 'year') && (
          <span
            aria-hidden
            className="absolute left-1/2 pointer-events-none select-none rounded-full uppercase"
            style={{
              top: -22,
              transform: 'translateX(-50%)',
              padding: '2px 8px',
              fontSize: 10,
              letterSpacing: '0.08em',
              fontFamily: 'system-ui, sans-serif',
              fontWeight: 600,
              color: isSelected ? '#fff' : '#8A8578',
              background: isSelected ? '#C9A24A' : 'rgba(255,255,255,0.85)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
              zIndex: 25,
              whiteSpace: 'nowrap',
            }}
          >
            {zone.type === 'destination' ? 'Titre' : 'Année'}
          </span>
        )}
        <EditableText
          text={text}
          editable={editableNow}
          onChange={onTextChange ?? (() => {})}
          onCommit={onStopEditing}
          spanRef={textRef}
        />

        {/* Delete button — top-right corner, visible when selected. Hidden for
            the mandatory destination/year title on the customer side (Fix 2);
            admins keep it. Resize handles stay for everyone. */}
        {isSelected && interactive && (
          <>
            {!isProtectedTitle && (
              <button
                className="absolute -top-3 -right-3 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center z-30 shadow-sm"
                // Stop the press from bubbling to the zone's drag handler
                // (handleDragStart calls preventDefault, which on touch
                // swallows the subsequent click and made delete a no-op).
                onMouseDown={(e) => { e.stopPropagation(); }}
                onPointerDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                title="Supprimer"
              >
                <TrashIcon />
              </button>
            )}
            <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-se-resize z-20" onMouseDown={handleResizeStart} />
            <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-nw-resize z-20" onMouseDown={handleResizeStart} />
            <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-sw-resize z-20" onMouseDown={handleResizeStart} />
          </>
        )}
      </div>
    </div>
  );
}

function ExtraTextOverlay({
  zone, fontScale, containerWidth, containerHeight,
  interactive, isSelected, onSelect, onChange, onDelete,
}: {
  zone: ExtraTextZone; fontScale: number; containerWidth: number; containerHeight: number;
  interactive: boolean; isSelected: boolean; onSelect: () => void;
  onChange: (updated: ExtraTextZone) => void; onDelete: () => void;
}) {
  const dragState = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Hooks BEFORE the early return (React #310 fix — same as the title zone).
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!interactive || !isSelected) return;
    e.preventDefault(); e.stopPropagation();
    dragState.current = { startX: e.clientX, startY: e.clientY, origX: zone.x, origY: zone.y };
    const handleMove = (ev: MouseEvent) => {
      if (!dragState.current) return;
      const dx = (ev.clientX - dragState.current.startX) / containerWidth;
      const dy = (ev.clientY - dragState.current.startY) / containerHeight;
      // The box hugs the text and is anchored by `align`, so the anchor itself
      // can travel the full safe width/height — no need to subtract the box
      // size (that's what used to pin a wide box near the left edge). Clamp the
      // anchor to the safe insets so the text stays inside the printable area.
      const minX = SAFE_INSET_X, maxX = 1 - SAFE_INSET_X;
      const minY = SAFE_INSET_Y, maxY = 1 - SAFE_INSET_Y;
      onChange({ ...zone, x: Math.max(minX, Math.min(maxX, dragState.current.origX + dx)), y: Math.max(minY, Math.min(maxY, dragState.current.origY + dy)) });
    };
    const handleUp = () => { dragState.current = null; window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
    window.addEventListener('mousemove', handleMove); window.addEventListener('mouseup', handleUp);
  }, [interactive, isSelected, zone, containerWidth, containerHeight, onChange]);

  // Center-anchored radial resize (see TextOverlay for the rationale). Here the
  // controlled value is the absolute fontSize, so the ratio multiplies the
  // font size directly, clamped to [12, 300] and rounded to an integer px.
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!interactive) return;
    e.preventDefault(); e.stopPropagation();
    const rect = boxRef.current?.getBoundingClientRect();
    const center = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : { x: e.clientX, y: e.clientY };
    const start = { x: e.clientX, y: e.clientY };
    const startSize = zone.fontSize;
    const handleMove = (ev: MouseEvent) => {
      const next = cornerResizeScale(center, start, { x: ev.clientX, y: ev.clientY }, startSize, 12, 300);
      onChange({ ...zone, fontSize: Math.round(next) });
    };
    const handleUp = () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
    window.addEventListener('mousemove', handleMove); window.addEventListener('mouseup', handleUp);
  }, [interactive, zone, onChange]);

  if (!zone.text) return null;
  const fontSize = Math.max(6, zone.fontSize * fontScale);
  // Box HUGS the text (width:max-content) so the frame + handles resize with
  // the font, and the small box can be dragged edge-to-edge. `align` decides
  // which edge the x anchor pins, mirrored by the PDF (see drawFrontCover).
  const align = zone.align ?? 'center';
  const anchorShift = align === 'center' ? '-50%' : align === 'right' ? '-100%' : '0';

  return (
    <div
      ref={boxRef}
      className={`absolute ${interactive ? 'cursor-pointer' : 'pointer-events-none'} select-none`}
      style={{
        left: `${zone.x * 100}%`, top: `${zone.y * 100}%`,
        transform: `translateX(${anchorShift})`,
        width: 'max-content',
        maxWidth: `${Math.min(0.96, zone.width ?? 0.8) * 100}%`,
        color: zone.color,
        fontSize: `${fontSize}px`,
        fontFamily: fontStackFor(toCssFontFamily(zone.fontFamily ?? null), zone.text),
        unicodeBidi: 'plaintext',
        fontWeight: 400, lineHeight: 1.1, letterSpacing: '0.01em',
        textAlign: align,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        outline: isSelected ? '2px dashed #10b981' : 'none',
        outlineOffset: '3px',
        zIndex: isSelected ? 10 : 1,
      }}
      onClick={interactive ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
      onMouseDown={interactive && isSelected ? handleDragStart : undefined}
    >
      <span style={{ overflow: 'visible' }}>{zone.text}</span>
      {isSelected && interactive && (
        <>
          <button
            className="absolute -top-3 -right-3 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center z-30 shadow-sm"
            // Stop the press from bubbling to this zone's drag handler
            // (handleDragStart calls preventDefault, which on touch swallows
            // the subsequent click — that's why the extra-text bin did
            // nothing). The resize handles already stop propagation the same
            // way; the delete button was the one control that didn't.
            onMouseDown={(e) => { e.stopPropagation(); }}
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Supprimer"
          >
            <TrashIcon />
          </button>
          <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-emerald-500 border border-white rounded-sm cursor-se-resize z-20" onMouseDown={handleResizeStart} />
          <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-emerald-500 border border-white rounded-sm cursor-nw-resize z-20" onMouseDown={handleResizeStart} />
          <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-emerald-500 border border-white rounded-sm cursor-sw-resize z-20" onMouseDown={handleResizeStart} />
        </>
      )}
    </div>
  );
}
